/* eslint-disable class-methods-use-this */
/* eslint-disable no-underscore-dangle */
/* eslint-disable no-param-reassign */

import Validators from './validators';
import Types from '../utils/type';
import { getPropByPath, getTargetValue } from '../utils';

export class ValidateRule {
    constructor(validators = [], ruleOptions = {}, config, message) {
        this._generateRule(validators, ruleOptions, config, message);
        this.conditionCbStack = [];
        this.getBase = (cfg, source, options) => {
            let base;
            if (!Types.empty(cfg.base)) {
                ({ base } = cfg);
            } else if (cfg.path) {
                const data = cfg.bSource === 'extra' ? options.extraData : source;
                base = getPropByPath(data, cfg.path).v;
            } else {
                throw new Error('缺少基础值');
            }
            return base;
        };
    }

    condition(cb) {
        let trustCb = cb;
        if (typeof trustCb === 'boolean') {
            trustCb = () => trustCb;
        }
        this.conditionCbStack.push(trustCb);
        return this;
    }

    handleBeforeValidate(value, source, options, config) {
        const effective = this.conditionCbStack.every(conditionCallback => conditionCallback(this, source, options.extraData, value));
        return { value: this._getValue(config, value, source, options), effective };
    }

    _getValue(config, value, source, options) {
        const data = config.source === 'extra' ? options.extraData : source;
        if (config.target) {
            if (Types.array(config.target)) {
                return getTargetValue(config.target, data).map(item => item.v);
            }
            return getTargetValue(config.target, data).v;
        }
        return value;
    }

    _generateRule(validators, ruleOptions, config, message) {
        const methods = [];
        // 兼容之前的构造方式，如果是对象，直接把对象的每一个属性赋值给rule
        if (!Types.array(validators)) {
            Object.entries(validators).forEach(([key, value]) => {
                this[key] = value;
            });
            return;
        }
        // 如果是数组，则由数组组合出
        validators.forEach(item => {
            if (Types.method(item)) {
                methods.push(item);
            } else {
                Object.entries(item).forEach(([key, value]) => {
                    this[key] = value;
                });
            }
        });
        // 如果validators的方法里面没有定义
        if (!this.validator) {
            const validate = (rule, value, cb, source, options) => {
                source = options.source || source;
                // 如果不传入false，就会处理
                if (ruleOptions.handleInput !== false) {
                    let effective;
                    // 获取校验的目标值以及条件是否通过
                    ({ value, effective } = this.handleBeforeValidate(value, source, options, config));
                    // 如果条件判断为不生效，直接通过
                    if (!effective) return cb();
                    // 如果不校验空，空的时候直接通过校验
                }
                if (!ruleOptions.validEmpty) {
                    if (Types.empty(value)) return cb();
                }
                if (ruleOptions.formatValue && Types.method(ruleOptions.formatValue)) {
                    value = ruleOptions.formatValue(value);
                }
                const methodsRes = methods.map(method => method(rule, value, source, options, config));
                const promiseRes = [];
                methodsRes.forEach(item => {
                    // 异步方法
                    if (item && item.then && typeof item.then === 'function') {
                        promiseRes.push(item);
                    }
                });
                if (methodsRes.every(res => res)) {
                    if (promiseRes.length) {
                        return Promise.all(promiseRes)
                            .then(() => {
                                cb();
                            })
                            .catch(e => {
                                cb(message || config.message || e || '校验不通过');
                            });
                    }
                    return cb();
                }
                return cb(message || config.message || '校验不通过');
            };
            this.validator = validate;
        }
    }
}

const typeMap = {
    number: '数字',
    integer: '整数',
    float: '小数',
    array: '数组',
    date: '日期',
    object: '对象',
    method: '函数',
    phone: '电话',
    url: '网址',
    email: '邮箱',
    hex: '十六位数字',
    idcard: '身份证',
    bool: '布尔',
    positive: '正数',
    positiveInteger: '正整数'
};

export default {
    isRequired(config, message) {
        if (typeof config === 'string' && !message) {
            message = config;
            config = {};
        } else {
            config = config || {};
        }
        return new ValidateRule([Validators.requiredValidFun, { required: true }], { validEmpty: true }, config, message);
    },
    typeValid(config, message = `请输入${typeMap[config.type] || '正确的'}类型`) {
        return new ValidateRule([Validators.typeValidFun], {}, config, message);
    },
    notIs(config, message = '当前值不合法') {
        return new ValidateRule([Validators.notValidFun], { validEmpty: true }, config, message);
    },
    notZero(config, message) {
        if (typeof config === 'string' && !message) {
            message = config;
            config = {};
        } else {
            message = message || '请输入非零数字';
        }
        return new ValidateRule([Validators.notValidFun], {}, { ...config, base: 0, stringToNumber: true }, message);
    },
    range(config, message = '超出数值范围') {
        config = Object.assign({ min: -Infinity, max: Infinity, le: true, ge: true }, config);
        return new ValidateRule([Validators.rangeValidFun], {}, config, message);
    },
    lenRange(config, message = `超出限制长度${config.min || 0}-${config.max || 'infinity'}`) {
        config = Object.assign({ min: 0, max: Infinity }, config);
        return new ValidateRule([Validators.lenRangeValidFun], {}, config, message);
    },
    integerLen(config, message = '整数位长度错误') {
        config = Object.assign({ len: Infinity, isEqual: false }, config);
        return new ValidateRule([Validators.intLenValidFun], {}, config, message);
    },
    decimalLen(config, message = '小数位长度错误') {
        config = Object.assign({ len: Infinity, isEqual: false }, config);
        return new ValidateRule([Validators.decimalValidFun], {}, config, message);
    },
    pattern(config, message = '校验不通过') {
        if (!Types.regexp(config.reg)) {
            throw new TypeError('错误的参数类型');
        }
        config.reg = new RegExp(config.reg);
        return new ValidateRule([Validators.patternValidFun], {}, config, message);
    },
    isMultiple(config, message = `请输入${config.base}的整数倍`) {
        const defaultBase = config.path ? undefined : 1;
        config = Object.assign({ base: defaultBase }, config);
        return new ValidateRule([Validators.multipleValidFun], {}, config, message);
    },
    inList(config = {}, message = `数值必须是${config.list.join('、')}中的一项`) {
        return new ValidateRule([Validators.inListValidFun], {}, config, message);
    },
    inLenList(config = {}, message = `数值长度必须是${config.list.join('、')}中的一项`) {
        return new ValidateRule(
            [Validators.inListValidFun],
            {
                formatValue: val => {
                    return val.length;
                }
            },
            config,
            message
        );
    },
    uniqArray(config = {}, message) {
        if (typeof config === 'string' && !message) {
            message = config;
            config = {};
        } else {
            message = message || '数组中存在重复项';
        }
        return new ValidateRule([Validators.uniqArrayValidFun], {}, config, message);
    },
    arrayType(config = {}, message = '数组内元素类型错误') {
        return new ValidateRule([Validators.arrayTypeValidFun], {}, config, message);
    },
    isAsync(config = { validEmpty: false }, message = '校验不通过') {
        return new ValidateRule([config.callback], { isAsync: true, validEmpty: config.validEmpty }, config, message);
    },
    vmValidate(config = {}, message = '') {
        return new ValidateRule([Validators.vmVliadFun], { handleInput: false, validEmpty: true }, config, message);
    },
    // lt：less than 小于
    // le：less than or equal to 小于等于
    // eq：equal to 等于
    // ne：not equal to 不等于
    // ge：greater than or equal to 大于等于
    // gt：greater than 大于
    compare(config = {}, message = '校验不通过') {
        return new ValidateRule([Validators.compareValidFun], {}, config, message);
    },
    compareTime(config = { unit: 'seconds' }, message = '校验不通过') {
        return new ValidateRule([Validators.compareTimeValidFun], {}, config, message);
    },
    notOverlap(config = {}, message = '区域内存在重合的值') {
        return new ValidateRule([Validators.notOverlap], {}, config, message);
    }
};
